import { useEffect, useState } from "react";
import AnimatedScroll from "../../components/AnimatedScroll/AnimatedScroll";
import { fetchEventsFromApi, resolveEventImageUrl } from "../../utils/contentApi";
import { safeJsonParse } from "../../utils/safeJson";

function formatDateLabel(value) {
  const safe = String(value || "").trim();
  if (!safe) {
    return "-";
  }

  const parsed = new Date(`${safe}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return safe;
  }

  return parsed.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function Events() {
  document.title = "Carpe Diem - Événements";

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxItem, setLightboxItem] = useState(null);

  const buildEventPeriod = (eventItem) => {
    const startLabel = formatDateLabel(eventItem?.startDate);
    const endLabel = formatDateLabel(eventItem?.endDate);

    if (startLabel !== "-" && endLabel !== "-") {
      return `Du ${startLabel} au ${endLabel}`;
    }

    if (endLabel !== "-") {
      return `Jusqu'au ${endLabel}`;
    }

    return "Date à venir";
  };

  const isPublicEvent = (eventItem) =>
    eventItem?.isActive !== false && eventItem?.category !== "daily-menu";

  useEffect(() => {
    if (!lightboxItem) {
      return;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setLightboxItem(null);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [lightboxItem]);

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async () => {
      setLoading(true);

      const cached = localStorage.getItem("carpeDiemEvents");
      if (cached) {
        const parsed = safeJsonParse(cached, []);
        if (isMounted && Array.isArray(parsed)) {
          setEvents(parsed.filter((eventItem) => isPublicEvent(eventItem)));
        }
      }

      try {
        const payload = await fetchEventsFromApi();
        if (!isMounted) {
          return;
        }

        setEvents(payload.filter((eventItem) => isPublicEvent(eventItem)));
        localStorage.setItem("carpeDiemEvents", JSON.stringify(payload));
      } catch (_error) {
        // Keep cache fallback.
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    const handleContentChanged = () => loadEvents();
    window.addEventListener("carpe-content-changed", handleContentChanged);

    return () => {
      isMounted = false;
      window.removeEventListener("carpe-content-changed", handleContentChanged);
    };
  }, []);

  return (
    <section className="events-page-section">
      <div className="events-page-shell WMContainer-sc">
        <AnimatedScroll>
          <div className="events-page-head">
            <p className="events-page-kicker">Carpe Diem</p>
            <h2 className="events-page-title">Nos événements</h2>
            <p className="events-page-subtitle">
              Découvrez les événements à venir et en cours au Carpe Diem.
            </p>
          </div>
        </AnimatedScroll>

        {loading ? (
          <p className="events-page-state">Chargement des événements...</p>
        ) : events.length === 0 ? (
          <p className="events-page-state">
            Aucun événement en cours pour le moment.
          </p>
        ) : (
          <div className="events-page-list">
            {events.map((eventItem, index) => (
              <AnimatedScroll key={eventItem.id}>
                <article
                  className={`events-page-row${index % 2 === 1 ? " is-reversed" : ""}`}
                >
                  <div className="events-page-visual">
                    <button
                      type="button"
                      className="events-page-media-button"
                      onClick={() =>
                        setLightboxItem({
                          imageUrl: resolveEventImageUrl(eventItem.imageUrl),
                          title: eventItem.title,
                          imageFit: eventItem.imageFit,
                          imagePosition: eventItem.imagePosition,
                        })
                      }
                      aria-label={`Ouvrir l'image ${eventItem.title} en grand`}
                    >
                      <img
                        className="events-page-media"
                        src={resolveEventImageUrl(eventItem.imageUrl)}
                        alt={eventItem.title}
                        loading="lazy"
                        style={{
                          objectFit: eventItem.imageFit || "cover",
                          objectPosition: eventItem.imagePosition || "center",
                        }}
                      />
                    </button>
                  </div>

                  <div className="events-page-content">
                    <p className="events-page-date">
                      {buildEventPeriod(eventItem)}
                    </p>
                    <h3>{eventItem.title}</h3>
                    <p className="events-page-description">
                      {eventItem.description ||
                        "Plus d'informations à venir sur cet événement."}
                    </p>
                  </div>
                </article>
              </AnimatedScroll>
            ))}
          </div>
        )}

        {lightboxItem ? (
          <div
            className="events-lightbox-overlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setLightboxItem(null)}
          >
            <figure
              className="events-lightbox-content"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="events-lightbox-close"
                aria-label="Fermer l'image"
                onClick={() => setLightboxItem(null)}
              >
                ×
              </button>

              <img
                className="events-lightbox-image"
                src={lightboxItem.imageUrl}
                alt={lightboxItem.title}
                style={{
                  objectFit: lightboxItem.imageFit || "cover",
                  objectPosition: lightboxItem.imagePosition || "center",
                }}
              />
              <figcaption className="events-lightbox-caption">
                {lightboxItem.title}
              </figcaption>
            </figure>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default Events;
